/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { isMobile, isIOS, isBrowser, mobileModel } from 'react-device-detect';
import { cn } from "@/lib/utils";

import { Navbar } from "@/containers/Header/Navbar";
import MobileFooter from '@/containers/Footer/MobileFooter'
import NotificationPermission from "@/components/NotificationPermissionComponent";
import CookiesComponent from "@/components/CookiesComponent";
import { profileAction, chatv2Action, baseAction } from "@/redux/actions"
import Shortcuts from "@/components/Shortcuts/KeyboardShortcuts";
import { useDebouncedCallback } from "use-debounce";
import { toast } from "sonner";
import { SupportBubble } from "@/components/SupportBubble/SupportBubble";

/**
 * PrivateLayoutWeb - Migrated from Material-UI to Tailwind CSS
 */
const PrivateLayoutWeb = ({ children, match }) => {
  const { success } = baseAction
  const dispatch = useDispatch();

  const scrollContainerWrapper = useRef();

  const { getClonedStatus } = useSelector(s => s.profile);

  useEffect(() => {
    dispatch(profileAction.getNotificationCount());
  }, []);

  // add routes in array to hide header on that route
  const ignoreRoute = ['/call', '/settings']
  const hideNavbar = ignoreRoute.some(value => match?.path?.includes(value))

  // Hide the floating support bubble inside detail/editor pages — the
  // bubble crowds canvas + editor content. It still shows on the main
  // list pages (Leads list, Templates list, Sequences list, etc.).
  //
  //   /sequences?seqId=…   → Sequence editor
  //   /templates?templateId=… → Template editor
  //   /leads/detail/…      → Lead dashboard
  const isInSequenceEditor =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/sequences") &&
    new URLSearchParams(window.location.search).has("seqId");
  const isInTemplateEditor =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/templates") &&
    new URLSearchParams(window.location.search).has("templateId");
  const isInLeadDetail =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/leads/detail");
  const hideSupportBubble =
    hideNavbar || isInSequenceEditor || isInTemplateEditor || isInLeadDetail;

  //Notification
  const [allowCookie, setAllowCookie] = useState(false);
  const [allowNotify, setAllowNotify] = useState(false);

  let cookie = localStorage.getItem('myPolicy');
  if (cookie !== "PrivacyPolicy") {
    setTimeout(() => {
      setAllowCookie(true);
    }, 2000);
  }

  const onAcceptCookie = () => {
    localStorage.setItem('myPolicy', 'PrivacyPolicy');
    setAllowCookie(false);
  }

  const onAcceptNotify = () => {
    setAllowNotify(false);
  }

  const onNotAcceptNotify = () => {
    setAllowNotify(false);
  }

  const { newState, cloningProgress, socketEventData } = useSelector(s => (s.workSpaceCloning))
  const [browserPopup, setBrowserPopup] = useState(true);
  
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (newState && cloningProgress !== 100) {
        setBrowserPopup(true);
        event.preventDefault();
        event.returnValue = 'Are you sure want to refresh';
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [newState, cloningProgress, getClonedStatus]);

  useEffect(() => {
    if (socketEventData?.trigger > 1) {
      toast.success(`Trigger ${socketEventData?.trigger} completed`);
      dispatch(success("CLONING_NOTIFY_DATA_RESET_S"));
    }
  }, [socketEventData]);

  const handleScrollStop = useDebouncedCallback(() => {
    scrollContainerWrapper?.current?.classList.add("hideScroll");
  }, 200);

  const handleScroll = () => {
    scrollContainerWrapper?.current?.classList.remove("hideScroll");
    handleScrollStop();
  };

  return (
    <div className={cn("h-100 flex flex-col shrink-0")}>
      <Shortcuts />
      
      {isIOS && isMobile && !isBrowser && mobileModel === "iPhone" && (
        <div className="notchGrid shrink-0" style={{ height: 'var(--sat)' }} />
      )}
      
      {!hideNavbar && (
        <div className="shrink-0">
          <Navbar />
        </div>
      )}

      <div className="flex-1 h-full fixed top-0 left-0 w-full">
        <div
          ref={scrollContainerWrapper}
          className={cn(
            "h-full w-full overflow-y-auto hideScroll",
            hideNavbar ? "mt-0" : "mt-12.5",
          )}
          onScroll={handleScroll}
        >
          {children}
        </div>
      </div>
      
      <CookiesComponent
        openStatus={false}
        message={
          <>
            <span>
              By clicking "ACCEPT", you agree to the storing of first- and third-party cookies.{' '}
              <a href="https://www.flozy.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                Read our cookie policy
              </a>
            </span>
          </>
        }
        actionMessage="Accept"
        onActionClick={onAcceptCookie}
      />
      
      <NotificationPermission
        openStatus={allowNotify}
        message={"We'd like to show you notifications."}
        actionMessage="ALLOW"
        actionMessage2="NO THANKS"
        onActionClick={onAcceptNotify}
        onActionClick2={onNotAcceptNotify}
      />
      
      {!hideSupportBubble && <SupportBubble />}
    </div>
  );
}

PrivateLayoutWeb.propTypes = {
  children: PropTypes.node,
  match: PropTypes.object,
};

export default PrivateLayoutWeb;
